import { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Mail,
  Phone,
  MapPin,
  Linkedin,
  FileText,
  Star,
  MoreVertical,
  Pencil,
  Trash2,
  Sparkles,
  ChevronRight,
  FileDown,
  Download,
  Eye,
  Loader2,
  Briefcase,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useCandidateWithDetails, useDeleteCandidate, useGenerateEmbedding, useDeleteDocument, useCandidateJobs, useAssignJobs, useRemoveJob, useSetPrimaryJob } from '../hooks';
import {
  SkillsManagementDialog,
  CVGeneratorDialog,
  ExperiencesSection,
  EducationsSection,
  DocumentPreviewDialog,
} from '../components';
import { JobSelector } from '../components/JobSelector';
import { documentsService } from '../services';
import type { CandidateDocument, CandidateWithDetails } from '../types';
import { useDepartements, useRegions, useCountries } from '@/hooks/useGeography';

/**
 * Affiche la localisation structurée : ville, département, région, pays
 */
function CandidateLocation({ candidate }: { candidate: CandidateWithDetails }) {
  const { data: departements = [] } = useDepartements();
  const { data: regions = [] } = useRegions();
  const { data: countries = [] } = useCountries();

  const parts: string[] = [];

  if (candidate.locationCity) {
    parts.push(candidate.locationCity);
  }
  if (candidate.departementId) {
    const dept = departements.find((d) => d.id === candidate.departementId);
    if (dept) parts.push(dept.name);
  }
  if (candidate.regionId) {
    const reg = regions.find((r) => r.id === candidate.regionId);
    if (reg) parts.push(reg.name);
  }
  if (candidate.countryId) {
    const country = countries.find((c) => c.id === candidate.countryId);
    // N'afficher le pays que si ce n'est pas la France (implicite) ou s'il n'y a rien d'autre
    if (country && (country.alpha2 !== 'FR' || parts.length === 0)) {
      parts.push(country.nameFr);
    }
  }

  if (parts.length === 0) return null;

  return (
    <span className="flex items-center gap-1">
      <MapPin className="h-4 w-4" />
      {parts.join(', ')}
    </span>
  );
}

const seniorityLabels: Record<string, string> = {
  junior: 'Junior',
  mid: 'Confirmé',
  senior: 'Senior',
  expert: 'Expert',
  lead: 'Lead',
};

const availabilityLabels: Record<string, { label: string; color: string }> = {
  available: { label: 'Disponible immédiatement', color: 'bg-green-500' },
  open: { label: 'Ouvert aux opportunités', color: 'bg-yellow-500' },
  not_looking: { label: 'Non disponible', color: 'bg-gray-500' },
};

export function CandidateDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const candidateId = id ? parseInt(id) : undefined;
  const [skillsDialogOpen, setSkillsDialogOpen] = useState(false);
  const [cvGeneratorOpen, setCvGeneratorOpen] = useState(false);
  const [previewDoc, setPreviewDoc] = useState<CandidateDocument | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [deleteDocDialog, setDeleteDocDialog] = useState<CandidateDocument | null>(null);
  const [downloadingId, setDownloadingId] = useState<number | null>(null);

  const { data: candidate, isLoading, error } = useCandidateWithDetails(candidateId);
  const { data: candidateJobs = [] } = useCandidateJobs(candidateId);
  const deleteCandidate = useDeleteCandidate();
  const generateEmbedding = useGenerateEmbedding();
  const deleteDocument = useDeleteDocument();
  const assignJobs = useAssignJobs();
  const removeJob = useRemoveJob();
  const setPrimaryJob = useSetPrimaryJob();

  const handleDelete = async () => {
    if (!candidateId || !confirm('Êtes-vous sûr de vouloir supprimer ce candidat ?')) return;
    await deleteCandidate.mutateAsync(candidateId);
    navigate('/itc/candidates');
  };

  const handleGenerateEmbedding = async () => {
    if (!candidateId) return;
    await generateEmbedding.mutateAsync(candidateId);
  };

  const handleDocPreview = (doc: CandidateDocument) => {
    setPreviewDoc(doc);
    setPreviewOpen(true);
  };

  const handleDocDownload = async (doc: CandidateDocument) => {
    if (!candidateId) return;
    setDownloadingId(doc.id);
    try {
      const blob = await documentsService.downloadBlob(candidateId, doc.id);
      documentsService.triggerDownload(blob, doc.fileName);
    } catch {
      toast.error('Erreur lors du téléchargement');
    } finally {
      setDownloadingId(null);
    }
  };

  const handleDocDelete = async () => {
    if (!candidateId || !deleteDocDialog) return;
    try {
      await deleteDocument.mutateAsync({ candidateId, documentId: deleteDocDialog.id });
      toast.success('Document supprimé');
    } catch {
      toast.error('Erreur lors de la suppression');
    } finally {
      setDeleteDocDialog(null);
    }
  };

  if (isLoading) {
    return <CandidateDetailSkeleton />;
  }

  if (error || !candidate) {
    return (
      <div className="space-y-6">
        <Link to="/itc/candidates">
          <Button variant="ghost" size="sm">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Retour
          </Button>
        </Link>
        <Card className="border-destructive">
          <CardContent className="pt-6">
            <p className="text-destructive">Candidat non trouvé</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const initials = `${candidate.firstName[0]}${candidate.lastName[0]}`.toUpperCase();
  const fullName = `${candidate.firstName} ${candidate.lastName}`;
  const availability = candidate.availability ? availabilityLabels[candidate.availability] : null;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <Link to="/itc/candidates">
          <Button variant="ghost" size="sm">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Retour à la liste
          </Button>
        </Link>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="icon">
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => setCvGeneratorOpen(true)}>
              <FileDown className="mr-2 h-4 w-4" />
              Generer un CV
            </DropdownMenuItem>
            <DropdownMenuItem onClick={handleGenerateEmbedding}>
              <Sparkles className="mr-2 h-4 w-4" />
              Generer embedding IA
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <Link to={`/itc/candidates/${candidate.id}/edit`}>
              <DropdownMenuItem>
                <Pencil className="mr-2 h-4 w-4" />
                Modifier
              </DropdownMenuItem>
            </Link>
            <DropdownMenuItem className="text-destructive" onClick={handleDelete}>
              <Trash2 className="mr-2 h-4 w-4" />
              Supprimer
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Profile Header */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-6">
            <Avatar className="h-24 w-24">
              <AvatarFallback className="bg-primary/10 text-primary text-2xl font-semibold">
                {initials}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 space-y-4">
              <div>
                <div className="flex items-center gap-3">
                  <h1 className="text-2xl font-bold">{fullName}</h1>
                  {candidate.rating && candidate.rating > 0 && (
                    <div className="flex items-center gap-1 text-yellow-500">
                      <Star className="h-5 w-5 fill-current" />
                      <span className="font-medium">{candidate.rating}/5</span>
                    </div>
                  )}
                </div>
                {candidate.currentTitle && (
                  <p className="text-lg text-muted-foreground">{candidate.currentTitle}</p>
                )}
                {candidate.currentCompany && (
                  <p className="text-muted-foreground">chez {candidate.currentCompany}</p>
                )}
              </div>

              <div className="flex flex-wrap gap-2">
                {candidate.seniority && (
                  <Badge variant="outline" className="text-sm">
                    {seniorityLabels[candidate.seniority] || candidate.seniority}
                  </Badge>
                )}
                {availability && (
                  <Badge className={`${availability.color} text-white`}>
                    {availability.label}
                  </Badge>
                )}
                {candidate.yearsExperience && (
                  <Badge variant="secondary">{candidate.yearsExperience} ans d'expérience</Badge>
                )}
                {candidate.contractPreference && (
                  <Badge variant="outline">
                    {candidate.contractPreference === 'both' ? 'CDI + Freelance' : candidate.contractPreference === 'cdi' ? 'CDI' : 'Freelance'}
                  </Badge>
                )}
                {candidate.salaryExpectation && (
                  <Badge variant="secondary">{candidate.salaryExpectation.toLocaleString()} €/an</Badge>
                )}
                {candidate.tjmExpectation && (
                  <Badge variant="secondary">{candidate.tjmExpectation} €/jour</Badge>
                )}
              </div>

              <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                {candidate.email && (
                  <a href={`mailto:${candidate.email}`} className="flex items-center gap-1 hover:text-foreground">
                    <Mail className="h-4 w-4" />
                    {candidate.email}
                  </a>
                )}
                {candidate.phone && (
                  <a href={`tel:${candidate.phone}`} className="flex items-center gap-1 hover:text-foreground">
                    <Phone className="h-4 w-4" />
                    {candidate.phone}
                  </a>
                )}
                {(candidate.locationCity || candidate.regionId || candidate.countryId) && (
                  <CandidateLocation candidate={candidate} />
                )}
                {candidate.linkedinUrl && (
                  <a
                    href={candidate.linkedinUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 hover:text-foreground"
                  >
                    <Linkedin className="h-4 w-4" />
                    LinkedIn
                  </a>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary */}
      {candidate.summary && (
        <Card>
          <CardHeader>
            <CardTitle>Résumé</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="whitespace-pre-wrap">{candidate.summary}</p>
          </CardContent>
        </Card>
      )}

      {/* Jobs / Métiers */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Briefcase className="h-4 w-4" />
            Métiers
            {candidateJobs.length > 0 && (
              <span className="text-sm font-normal text-muted-foreground">
                ({candidateJobs.length})
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <JobSelector
            selectedJobs={candidateJobs}
            onAdd={(jobId) => {
              if (candidateId) {
                assignJobs.mutate({ candidateId, jobIds: [jobId] });
              }
            }}
            onRemove={(jobId) => {
              if (candidateId) {
                removeJob.mutate({ candidateId, jobId });
              }
            }}
            onSetPrimary={(jobId) => {
              if (candidateId) {
                setPrimaryJob.mutate({ candidateId, jobId });
              }
            }}
          />
        </CardContent>
      </Card>

      {/* Skills */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Sparkles className="h-4 w-4" />
            Compétences
            {candidate.skills && candidate.skills.length > 0 && (
              <span className="text-sm font-normal text-muted-foreground">
                ({candidate.skills.length})
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {candidate.skills && candidate.skills.length > 0 ? (
            <div className="flex flex-wrap items-center gap-2">
              {[...candidate.skills]
                .sort((a, b) => (b.isPrimary ? 1 : 0) - (a.isPrimary ? 1 : 0))
                .slice(0, 8)
                .map((skill) => (
                  <Badge key={skill.id} variant={skill.isPrimary ? 'default' : 'secondary'}>
                    {skill.skillName}
                  </Badge>
                ))}
              <Button
                variant="ghost"
                size="sm"
                className="h-6 px-2 text-xs"
                onClick={() => setSkillsDialogOpen(true)}
              >
                {candidate.skills.length > 8
                  ? `+${candidate.skills.length - 8} autres`
                  : 'Gérer'}
                <ChevronRight className="ml-1 h-3 w-3" />
              </Button>
            </div>
          ) : (
            <Button
              variant="ghost"
              size="sm"
              className="text-xs"
              onClick={() => setSkillsDialogOpen(true)}
            >
              Ajouter des compétences
            </Button>
          )}
        </CardContent>
      </Card>

      <SkillsManagementDialog
        open={skillsDialogOpen}
        onOpenChange={setSkillsDialogOpen}
        candidateId={candidate.id}
        skills={candidate.skills || []}
      />

      {/* Documents */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <FileText className="h-4 w-4" />
            Documents
            {candidate.documents && candidate.documents.length > 0 && (
              <span className="text-sm font-normal text-muted-foreground">
                ({candidate.documents.length})
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          {candidate.documents && candidate.documents.length > 0 ? (
            <div className="space-y-2">
              {candidate.documents.map((doc) => (
                <div
                  key={doc.id}
                  className="flex items-center justify-between gap-2 py-2 px-3 rounded-md border text-sm"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
                    <div className="min-w-0">
                      <span className="truncate block font-medium">{doc.fileName}</span>
                      {doc.fileSizeFormatted && (
                        <span className="text-xs text-muted-foreground">{doc.fileSizeFormatted}</span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      title="Aperçu"
                      onClick={() => handleDocPreview(doc)}
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      title="Télécharger"
                      disabled={downloadingId === doc.id}
                      onClick={() => handleDocDownload(doc)}
                    >
                      {downloadingId === doc.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Download className="h-4 w-4" />
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-destructive hover:text-destructive"
                      title="Supprimer"
                      onClick={() => setDeleteDocDialog(doc)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-muted-foreground text-sm">Aucun document</p>
          )}
        </CardContent>
      </Card>

      {/* Experiences & Educations */}
      <div className="grid gap-6 md:grid-cols-2">
        <ExperiencesSection candidateId={candidate.id} experiences={candidate.experiences || []} />
        <EducationsSection candidateId={candidate.id} educations={candidate.educations || []} />
      </div>

      <CVGeneratorDialog
        open={cvGeneratorOpen}
        onOpenChange={setCvGeneratorOpen}
        candidateId={candidate.id}
        candidateName={fullName}
      />

      {/* Document Preview Dialog */}
      {candidateId && (
        <DocumentPreviewDialog
          open={previewOpen}
          onOpenChange={setPreviewOpen}
          document={previewDoc}
          candidateId={candidateId}
        />
      )}

      {/* Delete Document Confirmation */}
      <AlertDialog open={!!deleteDocDialog} onOpenChange={(open) => !open && setDeleteDocDialog(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer le document</AlertDialogTitle>
            <AlertDialogDescription>
              Êtes-vous sûr de vouloir supprimer définitivement &quot;{deleteDocDialog?.fileName}&quot; ?
              Cette action est irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDocDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteDocument.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Suppression...
                </>
              ) : (
                'Supprimer'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function CandidateDetailSkeleton() {
  return (
    <div className="space-y-6">
      <Skeleton className="h-9 w-32" />
      <Card>
        <CardContent className="pt-6">
          <div className="flex gap-6">
            <Skeleton className="h-24 w-24 rounded-full" />
            <div className="flex-1 space-y-4">
              <Skeleton className="h-8 w-48" />
              <Skeleton className="h-5 w-64" />
              <div className="flex gap-2">
                <Skeleton className="h-6 w-20" />
                <Skeleton className="h-6 w-24" />
                <Skeleton className="h-6 w-28" />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
      <div className="grid gap-6 md:grid-cols-2">
        {[1, 2, 3, 4].map((i) => (
          <Card key={i}>
            <CardHeader>
              <Skeleton className="h-6 w-32" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-20 w-full" />
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
